import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, Flag, Check, CheckCircle2, RefreshCw, ChevronRight, AlertTriangle, Undo2 } from 'lucide-react'
import {
  getReviewSummary, getReviewBatch, decideReview, submitReview, abandonReview, undoLastReview,
  getFlagReasons,
} from '@/api/client'
import { AuthedImage } from '@/components/AuthedImage'
import { mediaPhotoUrl, mediaGeneratedUrl } from '@/lib/media'

const QUICK_REASONS = [
  'wrong item', 'low quality', 'artifacts', 'text/logo wrong',
  'wrong ingredients', 'bad framing', 'other',
]

const IMAGE_SIZE_KEY = 'review.imageSize'
const REASONS_KEY = 'review.flagReasons'
const MAX_REASONS = 12

function mergeReasons(...lists) {
  const seen = new Set()
  const out = []
  for (const list of lists) {
    for (const raw of list || []) {
      const reason = String(raw || '').trim()
      if (!reason) continue
      const key = reason.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      out.push(reason)
      if (out.length >= MAX_REASONS) return out
    }
  }
  return out
}

export function Review() {
  const [summary, setSummary] = useState(null)
  const [category, setCategory] = useState('')
  const [batch, setBatch] = useState(null)
  const [items, setItems] = useState([])
  const [decisions, setDecisions] = useState({}) // generation_id -> {decision, reason}
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [undoing, setUndoing] = useState(false)
  const [notice, setNotice] = useState(null)
  const [lastResult, setLastResult] = useState(null)
  const [imageSize, setImageSize] = useState(() => {
    const saved = Number(localStorage.getItem(IMAGE_SIZE_KEY))
    return Number.isFinite(saved) && saved >= 40 && saved <= 100 ? saved : 100
  })
  const [reasonOptions, setReasonOptions] = useState(() => {
    let saved = []
    try {
      saved = JSON.parse(localStorage.getItem(REASONS_KEY) || '[]')
    } catch {
      saved = []
    }
    return mergeReasons(saved, QUICK_REASONS)
  })

  useEffect(() => {
    localStorage.setItem(IMAGE_SIZE_KEY, String(imageSize))
  }, [imageSize])

  useEffect(() => {
    try {
      localStorage.setItem(REASONS_KEY, JSON.stringify(reasonOptions))
    } catch {
      // Ignore storage failures (private mode / quota) — the list still works in memory.
    }
  }, [reasonOptions])

  useEffect(() => {
    getFlagReasons({ limit: 50 })
      .then((r) => {
        const recent = r.data?.reasons || []
        setReasonOptions((prev) => mergeReasons(prev, recent, QUICK_REASONS))
      })
      .catch(() => {})
  }, [])

  const rememberReason = (reason) => {
    const cleaned = String(reason || '').trim()
    if (!cleaned) return
    setReasonOptions((prev) => mergeReasons([cleaned], prev, QUICK_REASONS))
  }

  const loadSummary = useCallback(async () => {
    const r = await getReviewSummary(category ? { categories: category } : {})
    setSummary(r.data)
  }, [category])

  const loadBatch = useCallback(async () => {
    setLoading(true)
    setLastResult(null)
    try {
      const r = await getReviewBatch(category ? { categories: category } : {})
      setBatch(r.data.batch)
      setItems(r.data.items || [])
      setDecisions({})
      await loadSummary()
    } finally {
      setLoading(false)
    }
  }, [category, loadSummary])

  useEffect(() => { loadBatch() }, [loadBatch])

  const remainingUnflagged = useMemo(
    () => items.filter((i) => (decisions[i.generation_id]?.decision) !== 'flagged').length,
    [items, decisions],
  )

  const setDecision = async (genId, decision, reason = null) => {
    setDecisions((prev) => ({ ...prev, [genId]: { decision, reason } }))
    try {
      await decideReview(batch.id, { generation_id: genId, decision, reason })
    } catch (e) {
      console.error('decision failed', e)
    }
  }

  const handleDecision = (genId, decision, reason = null) => {
    if (decision === 'flagged' && reason) rememberReason(reason)
    return setDecision(genId, decision, reason)
  }

  const handleSubmit = async () => {
    if (!batch) return
    setSubmitting(true)
    setNotice(null)
    try {
      const r = await submitReview(batch.id)
      await loadBatch()
      setLastResult(r.data)
    } catch (e) {
      setNotice({ kind: 'err', msg: e?.response?.data?.detail || 'Submit failed' })
    } finally {
      setSubmitting(false)
    }
  }

  const handleUndo = async () => {
    if (!window.confirm(
      'Reopen your last submitted batch? The current open batch (if any) will be discarded.',
    )) return
    setUndoing(true)
    setNotice(null)
    try {
      const r = await undoLastReview()
      setBatch(r.data.batch)
      setItems(r.data.items || [])
      setDecisions({})
      setLastResult(null)
      setNotice({ kind: 'ok', msg: 'Last submitted batch reopened — decisions cleared.' })
      await loadSummary()
    } catch (e) {
      setNotice({ kind: 'err', msg: e?.response?.data?.detail || 'Nothing to undo' })
    } finally {
      setUndoing(false)
    }
  }

  const handleCategoryChange = async (value) => {
    if (value === category) return
    if (batch && Object.keys(decisions).length > 0) {
      const ok = window.confirm(
        'Changing category will discard the decisions in the current batch. Continue?',
      )
      if (!ok) return
      try {
        await abandonReview(batch.id)
      } catch (e) {
        console.error('abandon failed', e)
      }
    }
    setCategory(value)
  }

  const cats = summary?.by_category || []

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Review photos</h1>
          <p className="text-gray-500">
            Flag anything that looks wrong. Un-flagged images are accepted and won’t appear again.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-400 uppercase mb-1">Category</label>
            <select
              data-testid="category-select"
              value={category}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white min-w-[180px]"
            >
              <option value="">All categories</option>
              {cats.map((c) => (
                <option key={c.category} value={c.category}>{c.category} ({c.pending})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 uppercase mb-1">
              Image size <span className="text-gray-500">{imageSize}%</span>
            </label>
            <div className="flex items-center h-[38px] px-1">
              <input
                data-testid="image-size"
                type="range"
                min="40"
                max="100"
                step="5"
                value={imageSize}
                onChange={(e) => setImageSize(Number(e.target.value))}
                className="w-32 accent-orange-500 cursor-pointer"
                title="Image size (saved in this browser)"
              />
            </div>
          </div>
          <button
            data-testid="reload-review"
            onClick={loadBatch}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" /> Reload
          </button>
          <button
            data-testid="undo-review"
            onClick={handleUndo}
            disabled={undoing}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50"
            title="Reopen the last batch you submitted"
          >
            {undoing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />}
            Undo last submit
          </button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Stat label="Available to review" value={summary.available} />
          <Stat label="In open batches" value={summary.reserved} />
          <Stat label="Flagged (all)" value={summary.flagged} />
          <Stat label="Accepted (all)" value={summary.accepted} />
        </div>
      )}

      {lastResult && (
        <div data-testid="review-result" className="mb-6 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" />
          Batch submitted — {lastResult.accepted} accepted, {lastResult.flagged} flagged. Next batch loaded.
        </div>
      )}

      {notice && (
        <div data-testid="review-notice" className={`mb-6 rounded-lg px-4 py-3 text-sm border ${
          notice.kind === 'ok'
            ? 'border-green-200 bg-green-50 text-green-800'
            : 'border-red-200 bg-red-50 text-red-700'
        }`}>
          {notice.msg}
        </div>
      )}

      {loading ? (
        <div className="py-24 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500 mx-auto" />
        </div>
      ) : items.length === 0 ? (
        <div className="py-24 text-center text-gray-500">
          <AlertTriangle className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          Nothing left to review in this selection. 🎉
        </div>
      ) : (
        <>
          <div className="space-y-8">
            {items.map((it) => (
              <ReviewCard
                key={it.generation_id}
                item={it}
                decision={decisions[it.generation_id]}
                onDecision={handleDecision}
                reasons={reasonOptions}
                imageSize={imageSize}
              />
            ))}
          </div>

          <div className="sticky bottom-4 mt-8 flex justify-center">
            <div className="bg-white shadow-lg border border-gray-200 rounded-full px-4 py-2 flex items-center gap-4">
              <span className="text-sm text-gray-600">
                {items.length} in batch · {remainingUnflagged} will be accepted
              </span>
              <button
                data-testid="submit-review"
                onClick={handleSubmit}
                disabled={submitting}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 disabled:opacity-50"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
                Submit review
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function Stat({ label, value }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">{label}</p>
      <p className="text-xl font-semibold text-gray-900 mt-0.5">{value ?? '—'}</p>
    </div>
  )
}

function ReviewCard({ item, decision, onDecision, reasons = QUICK_REASONS, imageSize = 100 }) {
  const state = decision?.decision
  const [showReasons, setShowReasons] = useState(false)
  const [reason, setReason] = useState(decision?.reason || '')

  const flagged = state === 'flagged'
  const accepted = state === 'accepted'
  const prevFlagged = !!(item.prev_flagged || item.prev_review_status === 'flagged')
  // Fill the viewport at 100%; scale down from there. The 260px reserve covers
  // the page header, card header and action row so a pair fills one screen.
  const imageHeight = `max(240px, calc((100vh - 260px) * ${imageSize / 100}))`

  const applyReason = (r) => {
    setReason(r)
    onDecision(item.generation_id, 'flagged', r)
    setShowReasons(false)
  }

  return (
    <div data-testid={`review-card-${item.item_id}`} data-item-name={item.name} className={`bg-white rounded-xl border overflow-hidden ${flagged ? 'border-amber-300 ring-2 ring-amber-200' : accepted ? 'border-green-200' : 'border-gray-200'}`}>
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-gray-900 truncate">{item.name}</p>
          <p className="text-xs text-gray-500 truncate">{item.restaurant_name} · {item.category}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {prevFlagged && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">re-review v{item.version}</span>
          )}
          {accepted && <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">accepted</span>}
          {flagged && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">flagged</span>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 p-3">
        <div>
          <p className="text-[11px] uppercase text-gray-400 mb-1">Original</p>
          <div className="w-full rounded-lg bg-gray-100 overflow-hidden" style={{ height: imageHeight }}>
            <AuthedImage src={mediaPhotoUrl(item.item_id)} alt={`${item.name} original`} className="w-full h-full object-contain" />
          </div>
        </div>
        <div>
          <p className="text-[11px] uppercase text-gray-400 mb-1">New (v{item.version})</p>
          <div className="w-full rounded-lg bg-gray-100 overflow-hidden" style={{ height: imageHeight }}>
            <AuthedImage src={mediaGeneratedUrl(item.generation_id)} alt={`${item.name} new`} className="w-full h-full object-contain" />
          </div>
        </div>
      </div>
      {prevFlagged && item.prev_generation_id && (
        <div className="px-3 pb-3">
          <p className="text-[11px] uppercase text-gray-400 mb-1">Previous (flagged)</p>
          <AuthedImage src={mediaGeneratedUrl(item.prev_generation_id)} alt="previous" className="w-20 h-20 object-cover rounded-lg bg-gray-100" />
        </div>
      )}
      <div className="px-3 pb-3">
        {showReasons ? (
          <div className="flex flex-wrap gap-1.5">
            {reasons.map((r) => (
              <button key={r} onClick={() => applyReason(r)}
                className="text-xs px-2 py-1 rounded-full bg-gray-100 hover:bg-amber-100 hover:text-amber-800">
                {r}
              </button>
            ))}
            <input
              data-testid={`reason-input-${item.item_id}`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applyReason(reason) }}
              placeholder="custom reason + Enter"
              className="text-xs px-2 py-1 rounded-full border border-gray-200 outline-none min-w-[140px]"
              autoFocus
            />
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              data-testid={`flag-button-${item.item_id}`}
              onClick={() => (flagged ? onDecision(item.generation_id, null) : setShowReasons(true))}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium ${
                flagged ? 'bg-amber-100 text-amber-800 hover:bg-amber-200' : 'bg-gray-100 text-gray-700 hover:bg-amber-50 hover:text-amber-800'
              }`}
            >
              <Flag className={`h-4 w-4 ${flagged ? 'fill-current' : ''}`} />
              {flagged ? 'Unflag' : 'Flag'}
            </button>
            {flagged && item.flag_reason && (
              <span className="text-xs text-amber-700 truncate max-w-[120px]" title={item.flag_reason}>“{item.flag_reason}”</span>
            )}
            {!flagged && (
              <button
                data-testid={`accept-button-${item.item_id}`}
                onClick={() => onDecision(item.generation_id, 'accepted')}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium ${accepted ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700 hover:bg-green-50 hover:text-green-700'}`}
                title="Mark as good (optional — unflagged items are accepted on submit)"
              >
                <Check className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
